import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FiPlus, FiEye, FiEdit2, FiTrash2, FiSearch, FiX, FiRefreshCw, FiCheckCircle, FiFilter } from 'react-icons/fi';
import Layout from '../components/Layout';
import Pagination from '../components/Pagination';
import ErrorBoundary from '../components/ErrorBoundary';
import { isTelegramDestination, remainPathAllowed } from '../utils/redirectDomain';
import { expandHexColor, groupRowStyle } from '../utils/redirectGroupColor';

const emptyForm = {
    domain: '',
    destinationUrl: '',
    name: '',
    active: true,
    remainPath: false,
    groupIds: [],
};

const authHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem('token')}`,
    'ngrok-skip-browser-warning': 'true',
});

const inputClass =
    'mt-1 block w-full rounded-md border border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-blue-700 font-bold py-2 px-3';

function formatLiveCheckedAt(iso) {
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleDateString('en-GB', {
        timeZone: 'Asia/Singapore',
        day: '2-digit',
        month: '2-digit',
        year: '2-digit',
    });
}

function liveFromDomainRow(row) {
    const last = row?.lastLive;
    if (!last?.checkedAt) return null;
    return {
        id: String(row._id),
        domain: row.domain,
        live: !!last.live,
        health: last.health,
        reason: last.reason,
        checkedAt: last.checkedAt,
    };
}

const RedirectDomainsPageContent = () => {
    const navigate = useNavigate();
    const [items, setItems] = useState([]);
    const [groups, setGroups] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalItems, setTotalItems] = useState(0);
    const [searchInput, setSearchInput] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [groupPickerQuery, setGroupPickerQuery] = useState('');
    const [appliedGroupIds, setAppliedGroupIds] = useState(null);
    const [draftGroupIds, setDraftGroupIds] = useState(new Set());
    const [filterOpen, setFilterOpen] = useState(false);
    const filterRef = useRef(null);
    const [activeFilter, setActiveFilter] = useState('all');
    const [sortBy, setSortBy] = useState('-createdAt');
    const [formData, setFormData] = useState(emptyForm);
    const [selected, setSelected] = useState(null);
    const [showCreate, setShowCreate] = useState(false);
    const [showEdit, setShowEdit] = useState(false);
    const [showView, setShowView] = useState(false);
    const [showDelete, setShowDelete] = useState(false);
    const [saving, setSaving] = useState(false);
    const [liveById, setLiveById] = useState({});
    const [liveChecked, setLiveChecked] = useState(false);
    const [liveRefreshing, setLiveRefreshing] = useState(false);
    const [liveRefreshingId, setLiveRefreshingId] = useState('');

    const fetchItems = async (page, search = '', active = 'all', groupIdList = null, sort = '-createdAt') => {
        try {
            setLoading(true);
            let apiUrl = `${process.env.REACT_APP_API_URL}/api/redirect-domains?page=${page}&limit=20`;
            if (search) apiUrl += `&search=${encodeURIComponent(search)}`;
            if (active !== 'all') apiUrl += `&active=${active}`;
            if (groupIdList !== null) {
                apiUrl += `&groupIds=${encodeURIComponent(groupIdList.length ? groupIdList.join(',') : 'none')}`;
            }
            if (sort) apiUrl += `&sort=${encodeURIComponent(sort)}`;
            const response = await axios.get(apiUrl, { headers: authHeaders() });
            if (response.data.success) {
                const rows = response.data.data || [];
                setItems(rows);
                setTotalPages(response.data.pagination.pages);
                setTotalItems(response.data.pagination.total);
                setLiveById((prev) => {
                    const next = { ...prev };
                    for (const row of rows) {
                        const fromDb = liveFromDomainRow(row);
                        if (fromDb) next[String(row._id)] = fromDb;
                    }
                    return next;
                });
            }
        } catch (err) {
            setError(`Failed to fetch domains: ${err.response?.data?.error || err.message}`);
        } finally {
            setLoading(false);
        }
    };

    const fetchGroups = async () => {
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/api/redirect-groups?limit=200&sort=groupName`,
                { headers: authHeaders() }
            );
            if (response.data.success) setGroups(response.data.data || []);
        } catch {
            // group dropdown stays empty
        }
    };

    const refreshLiveStatus = async () => {
        if (liveRefreshing || liveRefreshingId) return;
        try {
            setLiveRefreshing(true);
            setError('');
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/api/redirect-domains/live-status`,
                { headers: authHeaders() }
            );
            if (response.data.success) {
                const next = {};
                for (const row of response.data.data || []) {
                    if (row?.id) next[row.id] = row;
                }
                setLiveById(next);
                setLiveChecked(true);
            }
        } catch (err) {
            setError(`Failed to refresh live status: ${err.response?.data?.error || err.message}`);
        } finally {
            setLiveRefreshing(false);
        }
    };

    const refreshOneLiveStatus = async (domainId) => {
        const id = String(domainId || '');
        if (!id || liveRefreshing || liveRefreshingId) return;
        try {
            setLiveRefreshingId(id);
            setError('');
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/api/redirect-domains/${id}/live-status`,
                { headers: authHeaders() }
            );
            if (response.data.success && response.data.data) {
                setLiveById((prev) => ({ ...prev, [id]: response.data.data }));
            }
        } catch (err) {
            setError(`Failed to refresh live status: ${err.response?.data?.error || err.message}`);
        } finally {
            setLiveRefreshingId('');
        }
    };

    useEffect(() => {
        fetchItems(currentPage, searchTerm, activeFilter, appliedGroupIds, sortBy);
    }, [currentPage, searchTerm, activeFilter, appliedGroupIds, sortBy]);

    useEffect(() => {
        fetchGroups();
    }, []);

    useEffect(() => {
        if (!filterOpen) return undefined;
        const onPointerDown = (event) => {
            if (filterRef.current && !filterRef.current.contains(event.target)) {
                setFilterOpen(false);
            }
        };
        document.addEventListener('mousedown', onPointerDown);
        return () => document.removeEventListener('mousedown', onPointerDown);
    }, [filterOpen]);

    const allGroupIds = groups.map((g) => String(g._id));

    const openGroupFilter = () => {
        setDraftGroupIds(
            appliedGroupIds === null ? new Set(allGroupIds) : new Set(appliedGroupIds)
        );
        setFilterOpen((open) => !open);
    };

    const toggleDraftGroup = (id) => {
        setDraftGroupIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const saveGroupFilter = () => {
        const selected = [...draftGroupIds];
        const isAll =
            allGroupIds.length > 0 &&
            selected.length === allGroupIds.length &&
            allGroupIds.every((id) => draftGroupIds.has(id));
        setAppliedGroupIds(isAll || (allGroupIds.length === 0 && selected.length === 0) ? null : selected);
        setFilterOpen(false);
        setCurrentPage(1);
    };

    const closeModals = () => {
        setShowCreate(false);
        setShowEdit(false);
        setShowView(false);
        setShowDelete(false);
        setSelected(null);
        setFormData(emptyForm);
        setGroupPickerQuery('');
    };

    const fillForm = (row) => {
        setFormData({
            domain: row.domain || '',
            destinationUrl: row.destinationUrl || '',
            name: row.name || '',
            active: row.active !== false,
            remainPath: remainPathAllowed(row.destinationUrl) && !!row.remainPath,
            groupIds: (row.groups || []).map((g) => String(g._id)),
        });
        setGroupPickerQuery('');
    };

    const payloadFromForm = () => {
        const telegram = isTelegramDestination(formData.destinationUrl);
        return {
            ...formData,
            remainPath: telegram ? false : !!formData.remainPath,
            groupIds: formData.groupIds || [],
        };
    };

    const handleDestinationBlur = () => {
        if (isTelegramDestination(formData.destinationUrl) && formData.remainPath) {
            setFormData((prev) => ({ ...prev, remainPath: false }));
        }
    };

    const handleCreate = async (e) => {
        e.preventDefault();
        try {
            setSaving(true);
            await axios.post(
                `${process.env.REACT_APP_API_URL}/api/redirect-domains`,
                payloadFromForm(),
                { headers: authHeaders() }
            );
            closeModals();
            fetchItems(currentPage, searchTerm, activeFilter, appliedGroupIds, sortBy);
            fetchGroups();
        } catch (err) {
            setError(`Failed to create: ${err.response?.data?.error || err.message}`);
        } finally {
            setSaving(false);
        }
    };

    const handleEdit = async (e) => {
        e.preventDefault();
        if (!selected) return;
        try {
            setSaving(true);
            await axios.put(
                `${process.env.REACT_APP_API_URL}/api/redirect-domains/${selected._id}`,
                payloadFromForm(),
                { headers: authHeaders() }
            );
            closeModals();
            fetchItems(currentPage, searchTerm, activeFilter, appliedGroupIds, sortBy);
            fetchGroups();
        } catch (err) {
            setError(`Failed to update: ${err.response?.data?.error || err.message}`);
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!selected) return;
        try {
            setSaving(true);
            await axios.delete(
                `${process.env.REACT_APP_API_URL}/api/redirect-domains/${selected._id}`,
                { headers: authHeaders() }
            );
            closeModals();
            fetchItems(currentPage, searchTerm, activeFilter, appliedGroupIds, sortBy);
            fetchGroups();
        } catch (err) {
            setError(`Failed to delete: ${err.response?.data?.error || err.message}`);
        } finally {
            setSaving(false);
        }
    };

    const formFields = (
        <>
            <div>
                <label className="block text-sm font-medium text-gray-700">Domain</label>
                <input
                    type="text"
                    required
                    className={inputClass}
                    placeholder="abc.com"
                    value={formData.domain}
                    onChange={(e) => setFormData({ ...formData, domain: e.target.value })}
                />
            </div>
            <div>
                <label className="block text-sm font-medium text-gray-700">Destination URL</label>
                <input
                    type="text"
                    required
                    className={inputClass}
                    placeholder="https://efg.com or https://t.me/+xxxx"
                    value={formData.destinationUrl}
                    onChange={(e) => setFormData({ ...formData, destinationUrl: e.target.value })}
                    onBlur={handleDestinationBlur}
                />
            </div>
            <div>
                <label className="block text-sm font-medium text-gray-700">Name</label>
                <input
                    type="text"
                    className={inputClass}
                    placeholder="SLGWeb"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                />
                <p className="mt-1 text-xs text-gray-500">To search/show in ERP only</p>
            </div>
            <div>
                <label className="block text-sm font-medium text-gray-700">Groups</label>
                <input
                    type="text"
                    className={inputClass}
                    placeholder="Type group name to filter"
                    value={groupPickerQuery}
                    onChange={(e) => setGroupPickerQuery(e.target.value)}
                />
                <div className="mt-2 max-h-40 overflow-y-auto rounded-md border border-gray-200 divide-y">
                    {groups
                        .filter((g) =>
                            String(g.groupName || '')
                                .toLowerCase()
                                .includes(groupPickerQuery.trim().toLowerCase())
                        )
                        .map((g) => {
                            const id = String(g._id);
                            const checked = (formData.groupIds || []).includes(id);
                            return (
                                <label key={id} className="flex items-center gap-2 px-3 py-2 text-sm text-gray-800">
                                    <input
                                        type="checkbox"
                                        checked={checked}
                                        onChange={() => {
                                            setFormData((prev) => {
                                                const current = prev.groupIds || [];
                                                return {
                                                    ...prev,
                                                    groupIds: checked
                                                        ? current.filter((x) => x !== id)
                                                        : [...current, id],
                                                };
                                            });
                                        }}
                                    />
                                    <span
                                        className="inline-block h-3 w-3 rounded border border-gray-300"
                                        style={{ backgroundColor: expandHexColor(g.color) }}
                                    />
                                    {g.groupName}
                                </label>
                            );
                        })}
                    {!groups.filter((g) =>
                        String(g.groupName || '')
                            .toLowerCase()
                            .includes(groupPickerQuery.trim().toLowerCase())
                    ).length && (
                        <p className="px-3 py-2 text-sm text-gray-500">No matching groups</p>
                    )}
                </div>
                <p className="mt-1 text-xs text-gray-500">A domain can be in more than one group</p>
            </div>
            <label className="flex items-center gap-2 text-sm text-gray-700">
                <input
                    type="checkbox"
                    checked={formData.active}
                    onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                />
                Enable
            </label>
            <label
                className={`flex items-center gap-2 text-sm ${
                    remainPathAllowed(formData.destinationUrl) ? 'text-gray-700' : 'text-gray-400'
                }`}
            >
                <input
                    type="checkbox"
                    disabled={!remainPathAllowed(formData.destinationUrl)}
                    checked={remainPathAllowed(formData.destinationUrl) && formData.remainPath}
                    onChange={(e) => {
                        if (!remainPathAllowed(formData.destinationUrl)) return;
                        setFormData({ ...formData, remainPath: e.target.checked });
                    }}
                />
                Remain path (web only; not available for Telegram)
            </label>
        </>
    );

    return (
        <div className="space-y-6 w-full max-w-none px-8 py-6">
            <div className="bg-white rounded-lg shadow p-6">
                <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
                    <h2 className="text-xl font-semibold">Redirect Domains</h2>
                    <div className="flex flex-wrap items-center gap-2">
                        <button
                            type="button"
                            onClick={() => navigate('/redirect/groups', { state: { openCreate: true } })}
                            className="inline-flex items-center gap-2 px-4 py-2 rounded-md border border-gray-300 bg-white text-gray-800 hover:bg-gray-50"
                        >
                            <FiPlus /> Add Group
                        </button>
                        <button
                            type="button"
                            onClick={() => {
                                setFormData(emptyForm);
                                setGroupPickerQuery('');
                                setShowCreate(true);
                            }}
                            className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-blue-600 text-white hover:bg-blue-700"
                        >
                            <FiPlus /> Add domain
                        </button>
                        <button
                            type="button"
                            disabled={liveRefreshing || Boolean(liveRefreshingId)}
                            onClick={refreshLiveStatus}
                            className="inline-flex items-center gap-2 px-4 py-2 rounded-md border border-gray-300 bg-white text-gray-800 hover:bg-gray-50 disabled:opacity-50"
                        >
                            <FiRefreshCw className={liveRefreshing ? 'animate-spin' : ''} />
                            {liveRefreshing ? 'Checking…' : 'Refresh Live Status'}
                        </button>
                    </div>
                </div>

                {error && (
                    <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-md flex items-start justify-between gap-2">
                        <span>{error}</span>
                        <button type="button" onClick={() => setError('')} aria-label="Dismiss">
                            <FiX />
                        </button>
                    </div>
                )}

                <div className="flex flex-wrap gap-3 mb-4">
                    <div className="flex items-center gap-2">
                        <input
                            type="text"
                            className="rounded-md border border-gray-300 py-2 px-3"
                            placeholder="Search domain, name, destination"
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    setSearchTerm(searchInput);
                                    setCurrentPage(1);
                                }
                            }}
                        />
                        <button
                            type="button"
                            onClick={() => {
                                setSearchTerm(searchInput);
                                setCurrentPage(1);
                            }}
                            className="p-2 rounded-md bg-gray-100 hover:bg-gray-200"
                        >
                            <FiSearch />
                        </button>
                    </div>
                    <select
                        className="rounded-md border border-gray-300 py-2 px-3"
                        value={activeFilter}
                        onChange={(e) => {
                            setActiveFilter(e.target.value);
                            setCurrentPage(1);
                        }}
                    >
                        <option value="all">All status</option>
                        <option value="true">Enable</option>
                        <option value="false">Disable</option>
                    </select>
                    <div className="relative" ref={filterRef}>
                        <button
                            type="button"
                            onClick={openGroupFilter}
                            className="inline-flex items-center gap-2 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-800 hover:bg-gray-50"
                        >
                            <FiFilter className="w-4 h-4" />
                            Filter
                        </button>
                        {filterOpen && (
                            <div className="absolute left-0 z-20 mt-1 w-64 rounded-md border border-gray-200 bg-white shadow-lg">
                                <div className="max-h-56 overflow-y-auto p-2 space-y-1">
                                    {groups.length === 0 ? (
                                        <p className="text-sm text-gray-500 px-1 py-2">No groups</p>
                                    ) : (
                                        groups.map((g) => {
                                            const id = String(g._id);
                                            return (
                                                <label
                                                    key={id}
                                                    className="flex items-center gap-2 text-sm text-gray-700 px-1 py-1 rounded hover:bg-gray-50"
                                                >
                                                    <input
                                                        type="checkbox"
                                                        checked={draftGroupIds.has(id)}
                                                        onChange={() => toggleDraftGroup(id)}
                                                    />
                                                    <span
                                                        className="inline-block h-3 w-3 rounded border border-gray-300"
                                                        style={{ backgroundColor: expandHexColor(g.color) }}
                                                    />
                                                    <span className="truncate">{g.groupName}</span>
                                                </label>
                                            );
                                        })
                                    )}
                                </div>
                                <div className="border-t border-gray-200 p-2">
                                    <button
                                        type="button"
                                        onClick={saveGroupFilter}
                                        className="w-full rounded-md bg-blue-600 text-white text-sm py-1.5 hover:bg-blue-700"
                                    >
                                        Save
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                    <select
                        className="rounded-md border border-gray-300 py-2 px-3"
                        value={sortBy}
                        onChange={(e) => {
                            setSortBy(e.target.value);
                            setCurrentPage(1);
                        }}
                        title="Sort by"
                    >
                        <option value="-createdAt">Sort: Newest</option>
                        <option value="groupName">Sort: Group</option>
                    </select>
                    <span className="self-center text-sm text-gray-500">{totalItems} domains</span>
                </div>

                {loading ? (
                    <div className="flex justify-center py-12">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Domain</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Destination</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Groups</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Live</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Enable</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Remain path</th>
                                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {items.length === 0 ? (
                                    <tr>
                                        <td colSpan="8" className="px-4 py-8 text-center text-gray-500">
                                            No redirect domains
                                        </td>
                                    </tr>
                                ) : (
                                    items.map((row) => (
                                        <tr key={row._id} style={groupRowStyle(row.groups)}>
                                            <td className="px-4 py-3 font-medium text-gray-900">{row.domain}</td>
                                            <td className="px-4 py-3 text-blue-700 max-w-xs truncate">{row.destinationUrl}</td>
                                            <td className="px-4 py-3 text-gray-700">{row.name || '—'}</td>
                                            <td className="px-4 py-3">
                                                {(row.groups || []).length ? (
                                                    <div className="flex flex-wrap gap-1">
                                                        {(row.groups || []).map((g) => (
                                                            <span
                                                                key={g._id}
                                                                className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-xs font-medium text-gray-800"
                                                                style={{ backgroundColor: `${expandHexColor(g.color)}66` }}
                                                            >
                                                                {g.groupName}
                                                            </span>
                                                        ))}
                                                    </div>
                                                ) : (
                                                    '—'
                                                )}
                                            </td>
                                            <td className="px-4 py-3">
                                                {(() => {
                                                    const id = String(row._id);
                                                    const live = liveById[id];
                                                    const checking = liveRefreshingId === id;
                                                    const dateLabel = formatLiveCheckedAt(live?.checkedAt);
                                                    return (
                                                        <div className="flex items-center gap-1.5 whitespace-nowrap">
                                                            {live?.live ? (
                                                                <FiCheckCircle
                                                                    className="h-5 w-5 shrink-0 text-green-600"
                                                                    title="Health ok and 302 to destination host"
                                                                    aria-label="Live"
                                                                />
                                                            ) : live ? (
                                                                <span className="text-sm text-gray-700">
                                                                    {live.health}/{live.reason}
                                                                </span>
                                                            ) : null}
                                                            {dateLabel ? (
                                                                <span className="text-xs text-gray-500">({dateLabel})</span>
                                                            ) : null}
                                                            <button
                                                                type="button"
                                                                title="Refresh this domain"
                                                                disabled={liveRefreshing || Boolean(liveRefreshingId)}
                                                                onClick={() => refreshOneLiveStatus(id)}
                                                                className="p-0.5 text-gray-500 hover:text-blue-600 disabled:opacity-40"
                                                            >
                                                                <FiRefreshCw
                                                                    className={`h-4 w-4 ${checking ? 'animate-spin' : ''}`}
                                                                />
                                                            </button>
                                                        </div>
                                                    );
                                                })()}
                                            </td>
                                            <td className="px-4 py-3">
                                                <span className={row.active ? 'text-green-700' : 'text-gray-500'}>
                                                    {row.active ? 'Yes' : 'No'}
                                                </span>
                                            </td>
                                            <td className="px-4 py-3">{row.remainPath ? 'Yes' : 'No'}</td>
                                            <td className="px-4 py-3">
                                                <div className="flex justify-end gap-2">
                                                    <button
                                                        type="button"
                                                        title="View"
                                                        onClick={() => {
                                                            setSelected(row);
                                                            setShowView(true);
                                                        }}
                                                    >
                                                        <FiEye className="text-gray-600" />
                                                    </button>
                                                    <button
                                                        type="button"
                                                        title="Edit"
                                                        onClick={() => {
                                                            setSelected(row);
                                                            fillForm(row);
                                                            setShowEdit(true);
                                                        }}
                                                    >
                                                        <FiEdit2 className="text-blue-600" />
                                                    </button>
                                                    <button
                                                        type="button"
                                                        title="Delete"
                                                        onClick={() => {
                                                            setSelected(row);
                                                            setShowDelete(true);
                                                        }}
                                                    >
                                                        <FiTrash2 className="text-red-600" />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                )}

                {totalPages > 1 && (
                    <Pagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={setCurrentPage}
                    />
                )}
            </div>

            {(showCreate || showEdit) && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-lg p-6">
                        <h3 className="text-lg font-semibold mb-4">
                            {showCreate ? 'Add redirect domain' : 'Edit redirect domain'}
                        </h3>
                        <form onSubmit={showCreate ? handleCreate : handleEdit} className="space-y-4">
                            {formFields}
                            <div className="flex justify-end gap-2 pt-2">
                                <button type="button" onClick={closeModals} className="px-4 py-2 rounded-md border">
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={saving}
                                    className="px-4 py-2 rounded-md bg-blue-600 text-white disabled:opacity-50"
                                >
                                    {saving ? 'Saving…' : 'Save'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {showView && selected && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-lg p-6 space-y-3">
                        <h3 className="text-lg font-semibold">Redirect domain</h3>
                        <p><span className="text-gray-500">Domain:</span> {selected.domain}</p>
                        <p className="break-all"><span className="text-gray-500">Destination:</span> {selected.destinationUrl}</p>
                        <p><span className="text-gray-500">Name:</span> {selected.name || '—'}</p>
                        <p>
                            <span className="text-gray-500">Groups:</span>{' '}
                            {(selected.groups || []).length
                                ? (selected.groups || []).map((g) => g.groupName).join(', ')
                                : '—'}
                        </p>
                        <p>
                            <span className="text-gray-500">Live:</span>{' '}
                            {(() => {
                                const live = liveById[String(selected._id)];
                                if (!live) return 'Not checked';
                                const dateLabel = formatLiveCheckedAt(live.checkedAt);
                                const status = live.live ? 'Yes' : `${live.health}/${live.reason}`;
                                return dateLabel ? `${status} (${dateLabel})` : status;
                            })()}
                        </p>
                        <p><span className="text-gray-500">Enable:</span> {selected.active ? 'Yes' : 'No'}</p>
                        <p><span className="text-gray-500">Remain path:</span> {selected.remainPath ? 'Yes' : 'No'}</p>
                        <div className="flex justify-end">
                            <button type="button" onClick={closeModals} className="px-4 py-2 rounded-md border">
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {showDelete && selected && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
                        <h3 className="text-lg font-semibold mb-2">Delete redirect domain?</h3>
                        <p className="text-gray-600 mb-6">
                            This removes <span className="font-medium">{selected.domain}</span> from redirectProject.
                            Visitors will get 404 until you add it again. Cache can take up to 60 seconds.
                        </p>
                        <div className="flex justify-end gap-2">
                            <button type="button" onClick={closeModals} className="px-4 py-2 rounded-md border">
                                Cancel
                            </button>
                            <button
                                type="button"
                                disabled={saving}
                                onClick={handleDelete}
                                className="px-4 py-2 rounded-md bg-red-600 text-white disabled:opacity-50"
                            >
                                {saving ? 'Deleting…' : 'Delete'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

const RedirectDomainsPage = () => (
    <Layout>
        <ErrorBoundary
            fallbackTitle="Redirect Domains Error"
            fallbackDescription="An error occurred while loading redirect domains."
        >
            <RedirectDomainsPageContent />
        </ErrorBoundary>
    </Layout>
);

export default RedirectDomainsPage;
